import { useContext, useMemo } from 'react';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import Container from 'components/Container';
import { SEO } from 'components/SEO';
import {
  createLocalBusinessSchema,
  createOrganizationSchema,
  createWebsiteSchema,
} from 'components/SEO/structuredData';

import { SchoolContext, SchoolLocation } from 'context/SchoolContext';

import { Contact } from 'pages/Main/components';

import { CoursesRequiredForLicense, Hero, Packages } from './components';
import Testimonials from './components/Testimonials';

const Home = (): JSX.Element => {
  const theme = useTheme();
  const schoolContext = useContext(SchoolContext);

  // Generate structured data for SEO
  const structuredData = useMemo(() => {
    if (!schoolContext) return [];

    const schemas = [];

    // Website schema
    const websiteSchema = createWebsiteSchema(
      schoolContext.name || 'Driver Education',
      `https://${window.location.hostname}`,
      schoolContext.missionStatement,
    );
    schemas.push(websiteSchema);

    // Organization schema
    const orgSchema = createOrganizationSchema(
      schoolContext.name || 'Driver Education',
      `https://${window.location.hostname}`,
      schoolContext.logoImage,
      schoolContext.locations,
      schoolContext.missionStatement,
    );
    schemas.push(orgSchema);

    // LocalBusiness schema for each location
    if (schoolContext.locations && schoolContext.locations.length > 0) {
      schoolContext.locations.forEach((location) => {
        const businessSchema = createLocalBusinessSchema({
          name: `${schoolContext.name} - ${location.city}`,
          description: schoolContext.missionStatement,
          url: `https://${window.location.hostname}`,
          telephone: location.phone,
          email: location.email,
          logo: schoolContext.logoImage,
          image: schoolContext.bannerImage,
          ...(location.streetAddress &&
            location.city &&
            location.state &&
            location.zip && {
              address: {
                streetAddress: location.streetAddress,
                addressLocality: location.city,
                addressRegion: location.state,
                postalCode: location.zip,
                addressCountry: 'US',
              },
            }),
          openingHours: ['Mo-Fr 08:00-17:00', 'Sa 09:00-15:00'],
          priceRange: '$-$$',
          ...(location.city &&
            location.state && {
              areaServed: [location.city, location.state],
            }),
        });
        schemas.push(businessSchema);
      });
    }

    return schemas;
  }, [schoolContext]);

  // Generate SEO keywords based on school context
  const seoKeywords = useMemo(() => {
    const baseKeywords = [
      'driver education',
      'driving school',
      'driving lessons',
      'drivers license',
      'behind the wheel training',
      'driver training',
      'driving instructor',
      'best driving school',
      'bmv certified driving school',
      'teen drivers ed',
      'defensive driving',
      'affordable drivers ed',
    ];

    if (schoolContext?.locations) {
      schoolContext.locations.forEach((location) => {
        // City + State keywords
        baseKeywords.push(
          `driving school ${location.city}`,
          `driver education ${location.city}`,
          `driving lessons ${location.city} ${location.state}`,
          `drivers ed ${location.city}`,
          `affordable drivers ed ${location.city}`,
          `best driving school ${location.city}`,
          `bmv certified driving school ${location.city}`,
          `teen drivers ed ${location.city}`,
          `defensive driving ${location.city}`,
        );

        // State-level keywords
        if (location.state) {
          baseKeywords.push(
            `drivers ed ${location.state}`,
            `${location.state} driving school`,
            `affordable drivers ed in ${location.state}`,
          );
        }
      });
    }

    if (schoolContext?.name) {
      baseKeywords.push(schoolContext.name.toLowerCase());
    }

    return baseKeywords;
  }, [schoolContext]);

  return (
    <>
      <SEO
        title="Driver Education & Training"
        description={
          schoolContext?.missionStatement ||
          'Professional driver education services with certified instructors, behind-the-wheel training, and comprehensive driving courses to help you get your license safely.'
        }
        keywords={seoKeywords}
        ogType="website"
        ogImage={schoolContext?.bannerImage || schoolContext?.logoImage}
        ogImageAlt={`${schoolContext?.name || 'Driver Education'} - Professional Driving School`}
        structuredData={structuredData}
      />
      {/* <Main navBarItems={navItems}> */}
      <Box
        bgcolor={'alternate.main'}
        sx={{
          position: 'relative',
          '&::after': {
            position: 'absolute',
            content: '""',
            width: '30%',
            zIndex: 1,
            top: 0,
            left: '5%',
            height: '100%',
            backgroundSize: '18px 18px',
            backgroundImage: `radial-gradient(${alpha(theme.palette.primary.dark, 0.4)} 20%, transparent 20%)`,
            opacity: 0.2,
          },
        }}
      >
        <Box position={'relative'} zIndex={3}>
          <Hero
            name={schoolContext?.name}
            missionStatement={schoolContext?.missionStatement}
            logoImage={schoolContext?.logoImage}
            image={schoolContext?.bannerImage}
          />
        </Box>
      </Box>

      <Container>
        <Packages packages={schoolContext?.packages} />
      </Container>

      <Container>
        <CoursesRequiredForLicense
          title="What You Need To Get Your Drivers License"
          // description="Here us where you can provide a description of what is usually required to obtain a drivers license and what courses need to be taken and in what order."
          courses={schoolContext?.coursesRequiredForLicense || []}
        />
      </Container>

      {(schoolContext?.testimonials?.length ?? 0) > 0 && (
        <Container>
          <Testimonials />
        </Container>
      )}

      <Box bgcolor={theme.palette.alternate.main} pt={4}>
        <Typography marginBottom={2} variant="h3" align={'center'} sx={{ fontWeight: 700 }}>
          Contact Us
        </Typography>
        <Container>
          <Stack gap={2} divider={<Divider flexItem />}>
            {schoolContext?.locations?.map((location: SchoolLocation, index: number) => (
              <Contact {...location} key={index} />
            ))}
          </Stack>
        </Container>
      </Box>

      {/* <Container>
        <Trucking />
      </Container>
      <About />
      <Container>
        <Features />
      </Container>
      <Box bgcolor={'alternate.main'}>
        <Container>
          <News />
        </Container>
      </Box>
      <Container>
        <Team />
      </Container>
      <Video />
      <Box bgcolor={'#11092d'}>
        <Container>
          <Integrations />
        </Container>
      </Box>
      <Container>
        <Pricings />
      </Container>
      <Box bgcolor={'alternate.main'}>
        <Container>
          <Jobs />
        </Container>
      </Box>
      <Container>
        <AboutBottom />
      </Container>
      <Box bgcolor={'primary.main'}>
        <Container>
          <Reviews />
        </Container>
      </Box> */}
      {/* </Main > */}
    </>
  );
};

export default Home;
